import { Component, Input } from '@angular/core';
import * as echarts from 'echarts';
import { ConfigService } from '../../shared-services/config.service';
import { auditTime, filter, Subject, takeUntil } from 'rxjs';
import { GlobalService } from '../../shared-services/global.service';
import { MatDialog } from '@angular/material/dialog';
import { PopupTableComponent } from '../../global-components/popup-table/popup-table.component';
import { DataErrorComponent } from "../../global-components/data-error/data-error.component";
import { CommonModule } from '@angular/common';
@Component({
  selector: 'lib-bar-bubble',
  imports: [DataErrorComponent, CommonModule],
  templateUrl: './bar-bubble.component.html',
  styleUrl: './bar-bubble.component.scss',
})
export class BarBubbleComponent {
  @Input() data!: any;
  private myChart!: echarts.ECharts;
  public hasError = false;
  public errorMessage = '';
  chartData: any;
  constructor(
    private configService: ConfigService,
    private filterState: GlobalService,
    private dialog: MatDialog
  ) {
  }

  private destroy$ = new Subject<void>();

  ngAfterViewInit() {
    this.filterState.filters$
          .pipe(
              // short delay to collect rapid emissions
              auditTime(1000),
              filter((filters) => filters !== null && filters !== undefined),
              takeUntil(this.destroy$)
            )
      .subscribe((filters) => {
        setTimeout(() => this.loadAndRenderChart(filters.filter), 0);
      });
  }

  private loadAndRenderChart(filters?: any): void {
    const chartDom = document.getElementById(this.data.widgetId);
    if (!chartDom) return;

    if (!this.myChart) {
      this.myChart = echarts.init(chartDom);
    }

    this.myChart.showLoading();

    this.configService.fetchData(this.data.apiUrl,this.data.functionName, filters, this.data)
      .subscribe({
        next: (res) => {
          this.chartData = this.data?.responseKey ? res?.responseList?.[0]?.[this.data?.responseKey] : res;         
          const isEmpty =
            !this.chartData ||
            !this.chartData?.length;

          if (isEmpty) {
            this.hasError = true;
            this.errorMessage = 'No data found for the selected filters.';
            this.myChart.clear();
          } else {
            this.hasError = false;
            this.renderChart(this.chartData, filters);
          }
          this.myChart.hideLoading();
          this.addResizeListener();
        },
        error: () => {
          this.myChart.showLoading();
          this.hasError = true;
          this.errorMessage = 'No Data available for the chart.';
          this.myChart?.clear();
        },
      });
  }

  private renderChart(chartDataReduced?: any, filters?: any) {
    // Extract yCategories sorted by totalCost
    const sortedCategories = [...chartDataReduced].sort(
      (a, b) => b.totalCost - a.totalCost
    );
    const yCategories = sortedCategories.map((c) => c.category);
    const totalsData = sortedCategories.map((c) => c.totalCost);
    // console.log('totalsData:', totalsData);

    // Extract x categories (all unique bucket names)
    const xSet = new Set<string>();
    chartDataReduced.forEach((c: { buckets: {} }) =>
      Object.keys(c.buckets).forEach((b) => xSet.add(b))
    );

    // const xCategories = Array.from(xSet).sort((a, b) => {
    //   const getFirstNumber = (str: string): number => {
    //     const match = str.match(/(\d+(\.\d+)?)/); // match first number (integer or decimal)
    //     return match ? parseFloat(match[0]) : -1; // fallback -1 if no number found
    //   };

    //   return getFirstNumber(a) - getFirstNumber(b);
    // });

    const xCategories = this.data.xAxis;


    // Create a map from category name to its index in yCategories
    const yIndexMap = yCategories.reduce((map: any, cat, idx) => {
      map[cat] = idx;
      return map;
    }, {} as Record<string, number>);

    // Prepare bubble data
    const seriesData: number[][] = [];
    chartDataReduced.forEach((c: any) => {
      const yi = yIndexMap[c.category]; // use mapped index
      Object.entries(c.buckets).forEach(([bucket, count]) => {
        const xi = xCategories.indexOf(bucket);
        if (xi !== -1) {
          seriesData.push([xi, yi, Number(count)]);
        }
      });
    });
    const scatterSizes = seriesData.map((d: any) => d[2]);
    const minSizeValue = Math.min(...scatterSizes);
    const maxSizeValue = Math.max(...scatterSizes);
    const option = {
      tooltip: {
        formatter: (params: any) => {
          if (params.seriesType === 'scatter') {
            const xLabel = xCategories[params.value[0]];
            const yLabel = yCategories[params.value[1]];
            const count = params.value[2];
            return `${yLabel} vs ${xLabel}<br/>Count: ${count}`;
          } else if (params.seriesType === 'bar') {
            return `${params.name}<br/>Total Cost: ${params.value} M`;
          }
          return;
        },
      },
      grid: [
        {
          left: '1%',
          top: '2%',
          bottom: '6%',
          width: '10%',
          containLabel: true,
        },
        {
          left: '15%',
          top: '2%',
          right: '1%',
          bottom: '1%',
          containLabel: true,
        },
      ],
      xAxis: [
        {
          type: 'value',
          gridIndex: 0,
          position: 'bottom',
          axisTick: { show: false },
          axisLine: { show: true, lineStyle: { opacity: 0 } },
          splitLine: { show: false },
          axisLabel: { show: false},
          name: 'Total Cost',
          nameLocation: 'middle',
          nameGap: 1,
          inverse: 'left',
          nameTextStyle: {
            fontSize: 12,
            fontWeight: 'bold',
            color: '#000'
          }
        },
        {
          type: 'category',
          gridIndex: 1,
          data: xCategories,
          axisLine: { show: true },
          splitLine: {
            show: true,
            lineStyle: { type: 'dotted' },
          },
        },
      ],
      yAxis: [
        {
          type: 'category',
          gridIndex: 0,
          data: yCategories,
          axisTick: { show: false },
          axisLine: { show: false },
          axisLabel: { show: false },
          splitLine: { show: false },
        },
        {
          type: 'category',
          gridIndex: 1,
          data: yCategories,
          axisTick: { show: false },
          axisLine: { show: true },
          splitLine: {
            show: true,
            lineStyle: { type: 'dotted' },
          },
          axisLabel: {
            show: true,
            formatter: function (value: string) {
              const key = value.replace(/\s+/g, '_');
              return `{${key}|${value}}`;
            },
            rich: chartDataReduced.reduce((styles: any, c: any) => {
              const key = c.category.replace(/\s+/g, '_');
              const score = c.score;
              styles[key] = {
                backgroundColor:
                  score > 0 && score < 4
                    ? '#c4312b'
                    : score >= 4 && score < 8
                      ? '#FFFF66'
                      : score >= 8 && score <= 10
                        ? '#4CAF50'
                        : '#adacac90',
                color: '#000000ff',
                align: 'center',
                padding: [10, 10],
                width: 100,
              };
              return styles;
            }, {}),
          },
        },
      ],

      
      series: [
        {
          name: 'Total Cost',
          type: 'bar',
          xAxisIndex: 0,
          yAxisIndex: 0,
          data: totalsData,
          label: {
            show: true,
            position: 'right',
            formatter: (params: any) => `${params.value} M`,
            color: (params: any) => params.value < 6 ? '#625f5fff' : '#fff',
          
          },
          itemStyle: {
            color: '#000000ff',
          },
        },
        {
          name: 'Bubble',
          type: 'scatter',
          xAxisIndex: 1,
          yAxisIndex: 1,
          symbolSize: (val: number[]) =>
            this.normalizeBubbleSize(val[2], minSizeValue, maxSizeValue),
          data: seriesData,
          label: {
            show: true,
            position: 'outside',
            formatter: (params: { value: any[] }) => params.value[2],
          },
          itemStyle: {
            color: new echarts.graphic.RadialGradient(0.4, 0.3, 1, [
              { offset: 0, color: '#ffffff' },
              { offset: 0.5, color: '#3F3F3F' },
              { offset: 1, color: '#2a2929ff' },
            ]),
          },
        },
      ],
    };
    this.myChart.setOption(option);
    this.myChart.off('click');
    this.myChart.on('click', (params: any) => {
      if (params.seriesType === 'scatter') {
        this.dialog.open(PopupTableComponent, {
          width: '1200px',
          height: '700px',
          maxWidth: '100vw',
          maxHeight: '100vh',
          data: {
            headers: [
              { key: 'VPCR NUMBER', label: 'VPCR Number' },
              { key: 'title', label: 'Title', width: 600 },
              { key: 'final_em', label: 'Engine Model' },
              { key: 'cleaned_vertical', label: 'Vertical' },
              { key: 'savings', label: 'Savings' },
              { key: 'total_cost', label: 'Total Cost' },
            ],
            filter: {
              cost_interval: [this.normalizeCostInterval(params.name)],
              final_phi_parameter: [yCategories[params.value[1]]],
              ...filters
            },
            apiUrl: this.data.tabelUrl,
            functionName: this.data.tableFunction
          },
        });
      }
    });
    setTimeout(() => this.myChart.hideLoading(), 1000);
  }
  


  private normalizeCostInterval(label: string): string {

    switch (label) {
      case '0 spend':
        return '0';
      case 'Betw. 0 and 50K':
        return '0-50K';
      case 'Betw. 50K and 100K':
        return '50K-100K';
      case 'Betw. 100K and 500K':
        return '100K-500K';
      case 'Betw. 500K and 1M':
        return '500K-1M';
      case 'More than 1M':
        return '>1M';
      default:
        return label;
    }
  }
  private addResizeListener(): void {
    window.addEventListener('resize', this.resizeChart);
  }
  private resizeChart = () => {
    this.myChart?.resize();
  };
  normalizeBubbleSize(
    value: number,
    minValue: number,
    maxValue: number,
    minSize = 13,
    maxSize = 50,
    scaleExponent = 0.5 // 0.5 = square root, <1 flattens, >1 exaggerates
  ): number {
    if (!value || maxValue === minValue) return (minSize + maxSize) / 2;

    // Normalize value between 0 and 1
    const normalized = (value - minValue) / (maxValue - minValue);

    // Apply exponent to control visual spacing
    const scaled = Math.pow(normalized, scaleExponent);

    return scaled * (maxSize - minSize) + minSize;
  }
  
  ngOnDestroy(): void {
    window.removeEventListener('resize', this.resizeChart);
    this.myChart?.dispose();
    this.destroy$.next();
    this.destroy$.complete();
  }
 
}


